// Customer Context for State Management

import { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react'
import { 
  Customer, 
  CreateCustomerRequest, 
  UpdateCustomerRequest, 
  CustomerFilters,
  CustomerSearchResult
} from '@/types/customers'
import {
  getCustomers,
  getCustomerById,
  createCustomer,
  updateCustomer,
  deleteCustomer,
  searchCustomers,
  getCustomersByCity,
  getCustomersByCountry
} from '@/utils/customers'
import { useAuth } from '@/contexts/AuthContext'

interface CustomerContextType {
  // State
  customers: Customer[]
  selectedCustomer: Customer | null
  loading: boolean
  error: string | null
  statistics: {
    totalCustomers: number
    activeCustomers: number
    inactiveCustomers: number
    newCustomersThisMonth: number
  }
  
  // Actions
  fetchCustomers: (filters?: CustomerFilters) => Promise<void>
  fetchCustomerById: (id: string) => Promise<Customer>
  createNewCustomer: (data: CreateCustomerRequest, pictureFile?: File | null) => Promise<Customer>
  updateExistingCustomer: (id: string, data: UpdateCustomerRequest, pictureFile?: File | null) => Promise<Customer>
  deleteExistingCustomer: (id: string) => Promise<void>
  searchCustomersByName: (query: string) => Promise<CustomerSearchResult[]>
  getCustomersByCityFilter: (city: string) => Promise<Customer[]>
  getCustomersByCountryFilter: (country: string) => Promise<Customer[]>
  fetchStatistics: () => Promise<void>
  
  // Utility
  clearError: () => void
  setSelectedCustomer: (customer: Customer | null) => void
}

const CustomerContext = createContext<CustomerContextType | undefined>(undefined)

export const useCustomers = () => {
  const context = useContext(CustomerContext)
  if (!context) {
    throw new Error('useCustomers must be used within a CustomerProvider')
  }
  return context
}

interface CustomerProviderProps {
  children: ReactNode
}

const calculateCustomerStatistics = (customerList: Customer[]) => {
  const now = new Date()
  const thisMonth = new Date(now.getFullYear(), now.getMonth(), 1)

  return {
    totalCustomers: customerList.length,
    activeCustomers: customerList.filter((customer) => customer.isActive).length,
    inactiveCustomers: customerList.filter((customer) => !customer.isActive).length,
    newCustomersThisMonth: customerList.filter((customer) => new Date(customer.createdAt) >= thisMonth).length
  }
}

export const CustomerProvider = ({ children }: CustomerProviderProps) => {
  // Get authentication state
  const { isAuthenticated } = useAuth()
  
  // State
  const [customers, setCustomers] = useState<Customer[]>([])
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [statistics, setStatistics] = useState({
    totalCustomers: 0,
    activeCustomers: 0,
    inactiveCustomers: 0,
    newCustomersThisMonth: 0
  })

  // Clear error
  const clearError = useCallback(() => {
    setError(null)
  }, [])

  // Fetch all customers
  const fetchCustomers = useCallback(async (filters?: CustomerFilters) => {
    // Only fetch if authenticated
    if (!isAuthenticated) {
      return
    }
    
    try {
      setLoading(true)
      setError(null)
      const data = await getCustomers(filters)
      setCustomers(data)
      setStatistics(calculateCustomerStatistics(data))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch customers')
    } finally {
      setLoading(false)
    }
  }, [isAuthenticated])

  // Fetch customer by ID
  const fetchCustomerById = useCallback(async (id: string): Promise<Customer> => {
    // Only fetch if authenticated
    if (!isAuthenticated) {
      throw new Error('Authentication required')
    }
    
    try {
      setLoading(true)
      setError(null)
      const customer = await getCustomerById(id)
      setSelectedCustomer(customer)
      return customer
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch customer'
      setError(errorMessage)
      throw new Error(errorMessage)
    } finally {
      setLoading(false)
    }
  }, [isAuthenticated])

  // Create new customer
  const createNewCustomer = useCallback(async (data: CreateCustomerRequest, pictureFile?: File | null): Promise<Customer> => {
    // Only create if authenticated
    if (!isAuthenticated) {
      throw new Error('Authentication required')
    }
    
    try {
      setLoading(true)
      setError(null)
      const newCustomer = await createCustomer(data, pictureFile)
      setCustomers(prev => {
        const nextCustomers = [newCustomer, ...prev]
        setStatistics(calculateCustomerStatistics(nextCustomers))
        return nextCustomers
      })
      return newCustomer
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create customer'
      setError(errorMessage)
      throw new Error(errorMessage)
    } finally {
      setLoading(false)
    }
  }, [isAuthenticated])

  // Update existing customer
  const updateExistingCustomer = useCallback(async (id: string, data: UpdateCustomerRequest, pictureFile?: File | null): Promise<Customer> => {
    // Only update if authenticated
    if (!isAuthenticated) {
      throw new Error('Authentication required')
    }
    
    try {
      setLoading(true)
      setError(null)
      const updatedCustomer = await updateCustomer(id, data, pictureFile)
      
      // Update customers list
      setCustomers(prev => {
        const nextCustomers = prev.map(c => c.id === id ? updatedCustomer : c)
        setStatistics(calculateCustomerStatistics(nextCustomers))
        return nextCustomers
      })
      
      // Update selected customer if it's the same one
      if (selectedCustomer?.id === id) {
        setSelectedCustomer(updatedCustomer)
      }
      
      return updatedCustomer
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update customer'
      setError(errorMessage)
      throw new Error(errorMessage)
    } finally {
      setLoading(false)
    }
  }, [isAuthenticated, selectedCustomer])

  // Archive existing customer
  const deleteExistingCustomer = useCallback(async (id: string) => {
    // Only delete if authenticated
    if (!isAuthenticated) {
      throw new Error('Authentication required')
    }
    
    try {
      setLoading(true)
      setError(null)
      await deleteCustomer(id)
      
      // Remove from customers list
      setCustomers(prev => {
        const nextCustomers = prev.filter(c => c.id !== id)
        setStatistics(calculateCustomerStatistics(nextCustomers))
        return nextCustomers
      })
      
      // Clear selected customer if it's the same one
      if (selectedCustomer?.id === id) {
        setSelectedCustomer(null)
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to archive customer'
      setError(errorMessage)
      throw new Error(errorMessage)
    } finally {
      setLoading(false)
    }
  }, [isAuthenticated, selectedCustomer])

  // Search customers
  const searchCustomersByName = useCallback(async (query: string): Promise<CustomerSearchResult[]> => {
    // Only search if authenticated
    if (!isAuthenticated) {
      throw new Error('Authentication required')
    }
    
    try {
      setError(null)
      return await searchCustomers(query)
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to search customers'
      setError(errorMessage)
      throw new Error(errorMessage)
    }
  }, [isAuthenticated])

  // Get customers by city
  const getCustomersByCityFilter = useCallback(async (city: string): Promise<Customer[]> => {
    // Only fetch if authenticated
    if (!isAuthenticated) {
      throw new Error('Authentication required')
    }
    
    try {
      setError(null)
      return await getCustomersByCity(city)
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch customers by city'
      setError(errorMessage)
      throw new Error(errorMessage)
    }
  }, [isAuthenticated])

  // Get customers by country
  const getCustomersByCountryFilter = useCallback(async (country: string): Promise<Customer[]> => {
    // Only fetch if authenticated
    if (!isAuthenticated) {
      throw new Error('Authentication required')
    }
    
    try {
      setError(null)
      return await getCustomersByCountry(country)
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch customers by country'
      setError(errorMessage)
      throw new Error(errorMessage)
    }
  }, [isAuthenticated])

  // Fetch statistics
  const fetchStatistics = useCallback(async () => {
    // Only fetch if authenticated
    if (!isAuthenticated) {
      return
    }
    
    try {
      setError(null)
      await fetchCustomers()
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch statistics'
      setError(errorMessage)
    }
  }, [fetchCustomers, isAuthenticated])

  // Load initial data only when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      fetchCustomers()
    } else {
      setCustomers([])
      setSelectedCustomer(null)
      setStatistics({
        totalCustomers: 0,
        activeCustomers: 0,
        inactiveCustomers: 0,
        newCustomersThisMonth: 0
      })
      setError(null)
    }
  }, [isAuthenticated, fetchCustomers])

  const value: CustomerContextType = {
    // State
    customers,
    selectedCustomer,
    loading,
    error,
    statistics,
    
    // Actions
    fetchCustomers,
    fetchCustomerById,
    createNewCustomer,
    updateExistingCustomer,
    deleteExistingCustomer,
    searchCustomersByName,
    getCustomersByCityFilter,
    getCustomersByCountryFilter,
    fetchStatistics,
    
    // Utility
    clearError,
    setSelectedCustomer,
  }

  return (
    <CustomerContext.Provider value={value}>
      {children}
    </CustomerContext.Provider>
  )
}
